import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Inbox, Mail, Plus, ShieldCheck, Trash2, UserPlus } from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/misc';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError, apiDelete, apiList, apiPatch, apiPost } from '@/lib/api';
import { formatDate, timeAgo } from '@/lib/format';
import { cn } from '@/lib/utils';

type PortalUser = {
  id: string;
  email: string;
  name: string;
  portalRole: 'ADMIN' | 'STANDARD';
  status: 'INVITED' | 'ACTIVE' | 'SUSPENDED';
  lastLoginAt: string | null;
  invitationExpires: string | null;
  createdAt: string;
  company: { id: string; name: string };
  contact: { id: string; firstName: string; lastName: string; designation: string | null } | null;
};

type PortalRequest = {
  id: string;
  requestType: string;
  subject: string;
  body: string;
  entityType: string | null;
  entityId: string | null;
  handledAt: string | null;
  createdAt: string;
  company: { id: string; name: string };
};

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'outline'> = {
  ACTIVE: 'default',
  INVITED: 'outline',
  SUSPENDED: 'secondary',
};

function usePortalUsers(companyId?: string) {
  return useQuery({
    queryKey: ['portal-users', companyId],
    queryFn: () =>
      apiList<PortalUser[]>('/portal-users', {
        params: companyId ? { companyId } : undefined,
      }),
  });
}

function usePortalRequests() {
  return useQuery({
    queryKey: ['portal-users', 'requests'],
    queryFn: () =>
      apiList<PortalRequest[]>('/portal-users/requests', {
        params: { handled: 'false' },
      }),
  });
}

function useCompanies() {
  return useQuery({
    queryKey: ['companies', 'options'],
    queryFn: () =>
      apiList<{ id: string; name: string }[]>('/companies', { params: { limit: 200 } }),
  });
}

/**
 * Who at each client can sign in to the portal (spec PTL-01).
 *
 * There is no self-registration anywhere in this product. A client account
 * exists because somebody inside the business created it from the company
 * record, which is the only place a person can be tied to the client they
 * belong to.
 */
export function PortalUsersPage() {
  const [companyId, setCompanyId] = useState('');
  const [inviting, setInviting] = useState(false);

  const { data, isLoading } = usePortalUsers(companyId || undefined);
  const { data: companies } = useCompanies();
  const { data: requests } = usePortalRequests();
  const queryClient = useQueryClient();

  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string; status?: string; portalRole?: string }) =>
      apiPatch<PortalUser>(`/portal-users/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal-users'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiDelete(`/portal-users/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal-users'] }),
  });

  const resend = useMutation({
    mutationFn: (id: string) => apiPost(`/portal-users/${id}/resend`, {}),
  });

  const markHandled = useMutation({
    mutationFn: (id: string) => apiPost(`/portal-users/requests/${id}/handled`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal-users'] }),
  });

  return (
    <div>
      <PageHeader
        title="Client portal access"
        description="Who at each client can sign in, and what they can see once they do."
        actions={
          <div className="flex items-center gap-2">
            <Select value={companyId} onChange={(event) => setCompanyId(event.target.value)} className="w-52">
              <option value="">Every client</option>
              {(companies?.data ?? []).map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </Select>
            <Button onClick={() => setInviting(true)}>
              <UserPlus className="mr-1.5 h-3.5 w-3.5" />
              Invite someone
            </Button>
          </div>
        }
      />

      {(requests?.data ?? []).length > 0 && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-base">Requests from clients</CardTitle>
            <CardDescription>
              Raised from the portal. Mark one handled once you have dealt with it.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {requests!.data.map((request) => (
              <div key={request.id} className="flex items-start gap-3 rounded-md border p-3">
                <Inbox className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{request.subject}</p>
                  <p className="text-xs text-muted-foreground">
                    {request.company.name} · {request.requestType} · {timeAgo(request.createdAt)}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                    {request.body}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => markHandled.mutate(request.id)}>
                  <Check className="mr-1.5 h-3.5 w-3.5" />
                  Handled
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={5} columns={4} />
            </div>
          ) : (data?.data ?? []).length === 0 ? (
            <div className="py-14 text-center">
              <ShieldCheck className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
              <p className="font-medium">Nobody has portal access yet</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                Inviting a client gives them their own sign-in, scoped to their company and nothing
                else.
              </p>
              <Button className="mt-5" onClick={() => setInviting(true)}>
                Invite the first one
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Person</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Access</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data!.data.map((user) => (
                  <TableRow key={user.id} className={cn(user.status === 'SUSPENDED' && 'opacity-60')}>
                    <TableCell>
                      <p className="text-sm font-medium">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </TableCell>
                    <TableCell className="text-sm">{user.company.name}</TableCell>
                    <TableCell>
                      <Select
                        value={user.portalRole}
                        onChange={(event) =>
                          update.mutate({ id: user.id, portalRole: event.target.value })
                        }
                        className="h-8 w-40 text-xs"
                      >
                        <option value="STANDARD">Standard — no finance</option>
                        <option value="ADMIN">Admin — sees invoices</option>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[user.status]}>{user.status.toLowerCase()}</Badge>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {user.lastLoginAt
                          ? `last in ${timeAgo(user.lastLoginAt)}`
                          : user.invitationExpires
                            ? `invite expires ${formatDate(user.invitationExpires)}`
                            : 'never signed in'}
                      </p>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {user.status === 'INVITED' && (
                          <Button size="sm" variant="ghost" onClick={() => resend.mutate(user.id)}>
                            <Mail className="mr-1 h-3.5 w-3.5" />
                            Resend
                          </Button>
                        )}
                        {user.status !== 'INVITED' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              update.mutate({
                                id: user.id,
                                status: user.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE',
                              })
                            }
                          >
                            {user.status === 'ACTIVE' ? 'Suspend' : 'Restore'}
                          </Button>
                        )}
                        <button
                          onClick={() => remove.mutate(user.id)}
                          className="p-1 text-muted-foreground hover:text-destructive"
                          title="Remove access"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {inviting && (
        <InviteDialog
          companies={companies?.data ?? []}
          defaultCompanyId={companyId}
          onClose={() => setInviting(false)}
        />
      )}
    </div>
  );
}

function InviteDialog({
  companies,
  defaultCompanyId,
  onClose,
}: {
  companies: { id: string; name: string }[];
  defaultCompanyId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [companyId, setCompanyId] = useState(defaultCompanyId);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [portalRole, setPortalRole] = useState<'ADMIN' | 'STANDARD'>('STANDARD');

  const invite = useMutation({
    mutationFn: (body: { companyId: string; name: string; email: string; portalRole: string }) =>
      apiPost<PortalUser>('/portal-users', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['portal-users'] });
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite someone to the portal</DialogTitle>
          <DialogDescription>
            They receive an email with a link to set their own password. It is good for seven days.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="invite-company">Which client?</Label>
            <Select
              id="invite-company"
              value={companyId}
              onChange={(event) => setCompanyId(event.target.value)}
            >
              <option value="">Choose a client</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="invite-name">Their name</Label>
            <Input id="invite-name" value={name} onChange={(event) => setName(event.target.value)} />
          </div>
          <div>
            <Label htmlFor="invite-email">Their email</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="invite-role">What can they see?</Label>
            <Select
              id="invite-role"
              value={portalRole}
              onChange={(event) => setPortalRole(event.target.value as 'ADMIN' | 'STANDARD')}
            >
              <option value="STANDARD">Standard — projects, tickets and documents</option>
              <option value="ADMIN">Admin — the above plus invoices and quotes</option>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              A standard user is refused every financial endpoint by the server, not merely shown a
              shorter menu.
            </p>
          </div>

          {invite.isError && <FormError message={(invite.error as ApiRequestError).message} />}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!companyId || !name.trim() || !email.trim() || invite.isPending}
            onClick={() => invite.mutate({ companyId, name, email, portalRole })}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Send the invitation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
